import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Persist a piece of UI state (tab id, section id, scope filter, ...)
 * in the URL query string so it survives page refresh AND can be
 * shared/bookmarked. Behaves like useState.
 *
 *   const [tab, setTab] = useUrlState("tab", "settings");
 *   // → URL becomes ...?tab=members when setTab("members") is called
 *   // → on refresh, useUrlState reads "members" back from the URL
 *
 * `defaultValue` is what's returned when the key is absent. When you
 * setTab(defaultValue), the key is removed from the URL (keeps URLs
 * clean for the common case).
 *
 * Multiple useUrlState hooks on the same page share the URL — they
 * coexist by writing only their own key.
 */
export default function useUrlState(key, defaultValue) {
  const [params, setParams] = useSearchParams();
  const value = params.get(key);
  const current = value == null ? defaultValue : value;

  const setValue = useCallback((next) => {
    setParams((prev) => {
      const out = new URLSearchParams(prev);
      // Resolver: support setValue(fn) like useState.
      const resolved = typeof next === "function" ? next(out.get(key) ?? defaultValue) : next;
      if (resolved == null || resolved === defaultValue || resolved === "") {
        out.delete(key);
      } else {
        out.set(key, String(resolved));
      }
      return out;
    }, { replace: true });
  }, [setParams, key, defaultValue]);

  return [current, setValue];
}
